import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SupportRequest,
  SupportRequestState,
} from "src/types/supportRequest.types";

const initialState: SupportRequestState = {
  list: { loading: false, data: null, error: null },
  detail: { loading: false, data: null, error: null },
  create: { loading: false, data: null, error: null },
  createAdmin: { loading: false, data: null, error: null },
  reply: { loading: false, data: null, error: null },
  updateStatus: { loading: false, data: null, error: null },
};

const supportRequestSlice = createSlice({
  name: "supportRequest",
  initialState,
  reducers: {
    // 📥 Fetch Support Requests
    getSupportRequestsListAdminRequest: (
      state,
      action: PayloadAction<{
        page: number;
        limit: number;
        status?: string;
        search?: string;
      }>
    ) => {
      state.list.loading = true;
      state.list.error = null;
    },
    getSupportRequestsListAdminSuccess: (state, action: PayloadAction<any>) => {
      state.list.loading = false;
      state.list.data = action.payload;
    },
    getSupportRequestsListAdminError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.list.loading = false;
      state.list.error = action.payload;
    },

    // 📥 Fetch Support Requests
    getSupportRequestsListTenantRequest: (
      state,
      action: PayloadAction<{
        page: number;
        limit: number;
        status?: string;
        search?: string;
      }>
    ) => {
      state.list.loading = true;
      state.list.error = null;
    },
    getSupportRequestsListTenantSuccess: (
      state,
      action: PayloadAction<any>
    ) => {
      state.list.loading = false;
      state.list.data = action.payload;
    },
    getSupportRequestsListTenantError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.list.loading = false;
      state.list.error = action.payload;
    },

    // 📥 Fetch a single ticket by id (admin)
    getSupportRequestByIdAdminRequest: (
      state,
      action: PayloadAction<string>
    ) => {
      state.detail.loading = true;
      state.detail.error = null;
    },
    getSupportRequestByIdAdminSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.detail.loading = false;
      state.detail.data = action.payload;
    },
    getSupportRequestByIdAdminError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.detail.loading = false;
      state.detail.error = action.payload;
    },

    // 📥 Fetch a single ticket by id (tenant)
    getSupportRequestByIdTenantRequest: (
      state,
      action: PayloadAction<string>
    ) => {
      state.detail.loading = true;
      state.detail.error = null;
    },
    getSupportRequestByIdTenantSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.detail.loading = false;
      state.detail.data = action.payload;
    },
    getSupportRequestByIdTenantError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.detail.loading = false;
      state.detail.error = action.payload;
    },
    resetSupportRequestDetail: (state) => {
      state.detail = { loading: false, data: null, error: null };
    },

    // ➕ Create Support Request (public "contact us")
    createSupportRequestRequest: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.create.loading = true;
      state.create.error = null;
    },
    createSupportRequestSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.create.loading = false;
      state.create.data = action.payload;
    },
    createSupportRequestError: (state, action: PayloadAction<string>) => {
      state.create.loading = false;
      state.create.error = action.payload;
    },
    resetCreateSupportRequest: (state) => {
      state.create = { loading: false, data: null, error: null };
    },

    // ➕ Raise a ticket as a logged-in tenant
    raiseSupportRequestRequest: (
      state,
      action: PayloadAction<{ subject?: string; message: string }>
    ) => {
      state.create.loading = true;
      state.create.error = null;
    },
    raiseSupportRequestSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.create.loading = false;
      state.create.data = action.payload;
    },
    raiseSupportRequestError: (state, action: PayloadAction<string>) => {
      state.create.loading = false;
      state.create.error = action.payload;
    },
    resetRaiseSupportRequest: (state) => {
      state.create = { loading: false, data: null, error: null };
    },

    // ➕ Superadmin opens a ticket (internal, or about a specific tenant)
    raiseSupportRequestAdminRequest: (
      state,
      action: PayloadAction<{ tenantId?: string; subject?: string; message: string }>
    ) => {
      state.createAdmin.loading = true;
      state.createAdmin.error = null;
    },
    raiseSupportRequestAdminSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.createAdmin.loading = false;
      state.createAdmin.data = action.payload;
    },
    raiseSupportRequestAdminError: (state, action: PayloadAction<string>) => {
      state.createAdmin.loading = false;
      state.createAdmin.error = action.payload;
    },
    resetRaiseSupportRequestAdmin: (state) => {
      state.createAdmin = { loading: false, data: null, error: null };
    },

    // 💬 Reply (admin)
    replySupportRequestAdminRequest: (
      state,
      action: PayloadAction<{ _id: string; message: string }>
    ) => {
      state.reply.loading = true;
      state.reply.error = null;
    },
    replySupportRequestAdminSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.reply.loading = false;
      state.reply.data = action.payload;
      state.detail.data = action.payload;
    },
    replySupportRequestAdminError: (state, action: PayloadAction<string>) => {
      state.reply.loading = false;
      state.reply.error = action.payload;
    },

    // 💬 Reply (tenant)
    replySupportRequestTenantRequest: (
      state,
      action: PayloadAction<{ _id: string; message: string }>
    ) => {
      state.reply.loading = true;
      state.reply.error = null;
    },
    replySupportRequestTenantSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.reply.loading = false;
      state.reply.data = action.payload;
      state.detail.data = action.payload;
    },
    replySupportRequestTenantError: (state, action: PayloadAction<string>) => {
      state.reply.loading = false;
      state.reply.error = action.payload;
    },
    resetReplySupportRequest: (state) => {
      state.reply = { loading: false, data: null, error: null };
    },

    // ✏️ Update Support Request Status
    updateSupportRequestStatusAdminRequest: (
      state,
      action: PayloadAction<{ _id: string; status: string }>
    ) => {
      state.updateStatus.loading = true;
      state.updateStatus.error = null;
    },
    updateSupportRequestStatusAdminSuccess: (
      state,
      action: PayloadAction<SupportRequest>
    ) => {
      state.updateStatus.loading = false;
      state.updateStatus.data = action.payload;
      if (state.detail.data?._id === action.payload._id) {
        state.detail.data = action.payload;
      }
    },
    updateSupportRequestStatusAdminError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.updateStatus.loading = false;
      state.updateStatus.error = action.payload;
    },
    resetUpdateSupportRequestStatusAdmin: (state) => {
      state.updateStatus = { loading: false, data: null, error: null };
    },
  },
});

export const {
  getSupportRequestsListAdminRequest,
  getSupportRequestsListAdminSuccess,
  getSupportRequestsListAdminError,

  getSupportRequestsListTenantRequest,
  getSupportRequestsListTenantSuccess,
  getSupportRequestsListTenantError,

  getSupportRequestByIdAdminRequest,
  getSupportRequestByIdAdminSuccess,
  getSupportRequestByIdAdminError,

  getSupportRequestByIdTenantRequest,
  getSupportRequestByIdTenantSuccess,
  getSupportRequestByIdTenantError,
  resetSupportRequestDetail,

  createSupportRequestRequest,
  createSupportRequestSuccess,
  createSupportRequestError,
  resetCreateSupportRequest,

  raiseSupportRequestRequest,
  raiseSupportRequestSuccess,
  raiseSupportRequestError,
  resetRaiseSupportRequest,

  raiseSupportRequestAdminRequest,
  raiseSupportRequestAdminSuccess,
  raiseSupportRequestAdminError,
  resetRaiseSupportRequestAdmin,

  replySupportRequestAdminRequest,
  replySupportRequestAdminSuccess,
  replySupportRequestAdminError,

  replySupportRequestTenantRequest,
  replySupportRequestTenantSuccess,
  replySupportRequestTenantError,
  resetReplySupportRequest,

  updateSupportRequestStatusAdminRequest,
  updateSupportRequestStatusAdminSuccess,
  updateSupportRequestStatusAdminError,
  resetUpdateSupportRequestStatusAdmin,
} = supportRequestSlice.actions;

export default supportRequestSlice.reducer;
